import { TradeInPreviewAcquisition } from '@carvana/merch-contracts';
import { useCallback, useContext, useEffect, useRef } from 'react';
import COOKIES from '~/constants/cookies';
import StaticContext from '~/context/common/StaticContext';
import TradeContext from '~/context/common/TradeContext';
import fetchTradeInPreviewAcquisitions from '~/services/common/fetchTradeInPreviewAcquisitions';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger, logInfo } from '~/utilities/loggers';

export default function useGetTrades(
  isLoggedIn: boolean,
  preferredAcquisitionNameFromQueryParam?: string
) {
  const {
    setSelectedTrade,
    trades: { setData, setError, setLoaded, setLoading }
  } = useContext(TradeContext);
  const { eventCorrelationId } = useContext(StaticContext);

  const queryParamAppliedRef = useRef<string | undefined>(undefined);

  const handleMultipleTrades = useCallback(
    (trades: TradeInPreviewAcquisition[]) => {
      const shouldApplyQueryParam =
        Boolean(preferredAcquisitionNameFromQueryParam) &&
        queryParamAppliedRef.current !== preferredAcquisitionNameFromQueryParam;

      const queryParamTrade = shouldApplyQueryParam
        ? trades.find(a => a.id === preferredAcquisitionNameFromQueryParam)
        : undefined;

      const cookieId = BrowserCookies.get(COOKIES.preferredTrade) ?? '';
      const cookieTrade = trades.find(a => a.id === cookieId);

      const selectedTrade = queryParamTrade ?? cookieTrade ?? trades[0];
      if (!selectedTrade) return;

      if (shouldApplyQueryParam) {
        if (queryParamTrade) {
          queryParamAppliedRef.current = preferredAcquisitionNameFromQueryParam;
          logInfo('Trade query param applied', {
            preferredAcquisitionName: preferredAcquisitionNameFromQueryParam
          });
        } else {
          logInfo('Trade acquisition name mismatch from query param', {
            availableTradeCount: trades.length,
            paramPreferredAcquisitionName:
              preferredAcquisitionNameFromQueryParam
          });
        }
      }

      if (selectedTrade.id !== cookieId) {
        BrowserCookies.set(COOKIES.preferredTrade, selectedTrade.id, {
          expires: 365
        });
      }

      setSelectedTrade(selectedTrade);
    },
    [preferredAcquisitionNameFromQueryParam, setSelectedTrade]
  );

  useEffect(() => {
    if (!isLoggedIn) {
      setLoaded();

      return;
    }

    setLoading();
    fetchTradeInPreviewAcquisitions(eventCorrelationId)
      .then(trades => {
        if (trades.length > 1) {
          handleMultipleTrades(trades);
        } else {
          if (
            preferredAcquisitionNameFromQueryParam &&
            !trades.some(
              trade => trade.id === preferredAcquisitionNameFromQueryParam
            )
          ) {
            logInfo('Trade acquisition name mismatch from query param', {
              availableTradeCount: trades.length,
              paramPreferredAcquisitionName:
                preferredAcquisitionNameFromQueryParam
            });
          }

          setSelectedTrade(trades.at(0) ?? null);
          BrowserCookies.remove(COOKIES.preferredTrade);
        }

        setData(trades);
      })
      .catch(generateErrorLogger('Get trades', {}, { callback: setError }));
  }, [
    eventCorrelationId,
    handleMultipleTrades,
    isLoggedIn,
    preferredAcquisitionNameFromQueryParam,
    setData,
    setError,
    setLoaded,
    setLoading,
    setSelectedTrade
  ]);
}
